/**
 * Hard-navigation helper for flows that must reload the whole document.
 *
 * Why this exists
 * ---------------
 * The auth screens deliberately do a full-document navigation
 * (`window.location.href = ...`) instead of a router push: the freshly-set
 * auth cookie has to be read by the middleware, which only happens on a fresh
 * document request.
 *
 * The problem is that those screens reach the assignment right after awaiting
 * a Server Action. The action's POST has already returned 200, but Next.js is
 * still streaming the rest of the response (the router tree refresh triggered
 * by the changed cookies). Tearing the document down mid-stream aborts that
 * in-flight fetch, and WebKit reports the aborted read as `TypeError: Load
 * failed` (Chromium: `Failed to fetch`). Nothing catches it, React 19 re-throws
 * it through `reportError()`, and it lands in `window.onerror` where Sentry's
 * global handler reports an error that is purely an artifact of a navigation we
 * asked for — LOCARORA-WEB-E, 427 events since 2026-03.
 *
 * `hardNavigate()` raises a window-level flag before assigning
 * `location.href`, so `beforeSendFilter` can call `isHardNavigationPending()`
 * and drop the network-abort noise for the brief moment between the assignment
 * and the document unload.
 *
 * Note the difference from locarora-admin's copy of this helper: there the
 * aborted stream surfaces through a React error boundary, so the boundary
 * itself checks the flag. Here it arrives via `window.onerror` without passing
 * any boundary, so the check lives in the Sentry filter instead.
 *
 * Client-safe: no server imports, and every access is guarded for SSR.
 */

declare global {
  interface Window {
    __lrHardNavPending?: boolean;
  }
}

/**
 * Raises the "this document is going away" flag without navigating.
 *
 * Pair it with `endDocumentTeardown()` on any path where the teardown turns
 * out not to happen, otherwise the still-open document would stay silent about
 * genuine errors. `installDocumentTeardownWatch()` already covers the common
 * recoveries (bfcache restore, returning to the foreground).
 *
 * No-op on the server.
 */
export function beginDocumentTeardown(): void {
  if (typeof window === "undefined") return;
  window.__lrHardNavPending = true;
}

/**
 * Clears the teardown flag, so the (still alive) document reports errors
 * normally again. No-op on the server.
 */
export function endDocumentTeardown(): void {
  if (typeof window === "undefined") return;
  window.__lrHardNavPending = false;
}

/**
 * Marks a hard navigation as pending and assigns `window.location.href`.
 * No-op on the server.
 */
export function hardNavigate(url: string): void {
  if (typeof window === "undefined") return;
  beginDocumentTeardown();
  window.location.href = url;
}

/**
 * `location.replace()` variant — same silencing contract, but without leaving
 * the current URL in the session history.
 * No-op on the server.
 */
export function hardReplace(url: string): void {
  if (typeof window === "undefined") return;
  beginDocumentTeardown();
  window.location.replace(url);
}

/**
 * `location.reload()` variant — used where the auth state changed but the
 * destination is the page the user is already on. Same silencing contract.
 * No-op on the server.
 */
export function hardReload(): void {
  if (typeof window === "undefined") return;
  beginDocumentTeardown();
  window.location.reload();
}

/**
 * True while a hard navigation is tearing the document down. The Sentry filter
 * uses this to skip reporting stream-abort noise.
 */
export function isHardNavigationPending(): boolean {
  if (typeof window === "undefined") return false;
  return window.__lrHardNavPending === true;
}

/**
 * Central safety net for the ~45 other `location.href` / `location.replace`
 * call sites we do not route through `hardNavigate()`, plus plain link clicks
 * and back/forward navigations.
 *
 * Both unload signals are needed because neither is reliable alone:
 * `beforeunload` is the one with guaranteed timing (it fires synchronously as
 * the navigation commits, before any network teardown) but WKWebView — the
 * platform this bug actually reproduces on — often skips it. `pagehide` does
 * fire there, but it ALSO fires when an iOS app is backgrounded, and in that
 * case the document may come back alive.
 *
 * So the flag has to be recoverable, and that is what the two restore paths
 * are for: `pageshow` (navigation cancelled, or the page returned from the
 * bfcache) and `visibilitychange` back to visible (the app was foregrounded
 * again). Without them a single background/foreground cycle would leave the
 * flag stuck and silence real errors for the rest of the session.
 *
 * Returns a teardown function; no-op (returns a no-op) on the server.
 */
export function installDocumentTeardownWatch(): () => void {
  if (typeof window === "undefined") return () => {};

  const begin = () => beginDocumentTeardown();
  const end = () => endDocumentTeardown();
  const endIfVisible = () => {
    if (document.visibilityState === "visible") endDocumentTeardown();
  };

  window.addEventListener("beforeunload", begin);
  window.addEventListener("pagehide", begin);
  window.addEventListener("pageshow", end);
  document.addEventListener("visibilitychange", endIfVisible);

  return () => {
    window.removeEventListener("beforeunload", begin);
    window.removeEventListener("pagehide", begin);
    window.removeEventListener("pageshow", end);
    document.removeEventListener("visibilitychange", endIfVisible);
  };
}
